import React, { useEffect, useRef, useState } from 'react';
import type { DonutView, GroupedBarsView, HBarsView, HeatmapView, SeriesView } from '../../../shared/analytics/types';
import { fmtEuro, fmtInt, fmtPct } from '../../../shared/analytics/compute';

/**
 * Graphiques du panneau Analyses — mêmes formes et mêmes couleurs que la
 * fiche « Analyses » du site : trait rose sur fond bordeaux, barres magenta /
 * ocre, heatmap rose, donut en dégradé de roses, barres horizontales.
 * SVG maison : léger, lisible, identique partout.
 */
export const PALETTE = {
  ink: '#3a1f20',
  line: '#eb97a8',
  magenta: '#d55181',
  ochre: '#c98500',
  // Rampe claire → foncée ; texte encre sur les 3 premières teintes, crème sur les 2 dernières (≥ 4.5:1).
  ordinal: ['#f6c9d1', '#eb97a8', '#de6f8a', '#b13f66', '#9f3358'],
  grid: 'rgba(255,245,243,0.10)',
  axis: 'rgba(255,245,243,0.55)',
  text: '#fff5f3',
};

function useWidth<T extends HTMLElement>(fallback = 640): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(Math.max(280, Math.round(el.clientWidth || fallback)));
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [fallback]);
  return [ref, w];
}

const Tip: React.FC<{ x: number; y: number; children: React.ReactNode }> = ({ x, y, children }) => (
  <div
    className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-xl bg-cream px-3 py-1.5 text-xs text-gray-900 shadow-lg whitespace-nowrap"
    style={{ left: x, top: y - 10 }}
  >
    {children}
  </div>
);

const axisText = { fill: PALETTE.axis, fontSize: 12, fontFamily: 'inherit' } as const;

/* ── Courbe + aire ─────────────────────────────────────────────────────── */
export const LineAreaChart: React.FC<{ view: SeriesView }> = ({ view }) => {
  const [ref, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const H = 300, L = 56, R = 16, T = 28, B = 36;
  // les points « pas encore mesurables » (null) ne sont pas tracés
  const drawn = view.points.map((p, i) => ({ ...p, i })).filter((p): p is { label: string; value: number; i: number } => p.value !== null);
  const vals = drawn.map((p) => p.value);
  const rawMax = Math.max(...vals, 1);
  const max = view.unit === 'percent' ? 100 : niceMax(rawMax);
  const step = view.unit === 'percent' ? 25 : max / 4;
  const n = view.points.length;
  const cx = (i: number) => L + (W - L - R) * (i + 0.5) / n;
  const cy = (v: number) => T + (H - T - B) * (1 - v / max);
  const fmtAxis = (v: number) => (view.unit === 'percent' ? `${v} %` : v >= 1000 ? `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k€` : v === 0 ? '0' : fmtEuro(v));
  const fmtVal = (v: number) => (view.unit === 'percent' ? fmtPct(v) : fmtEuro(v));
  const pts = drawn.map((p) => `${cx(p.i)},${cy(p.value)}`).join(' ');
  const first = drawn[0], last = drawn[drawn.length - 1];
  const area = drawn.length ? `M${cx(first.i)},${cy(0)} L${drawn.map((p) => `${cx(p.i)},${cy(p.value)}`).join(' L')} L${cx(last.i)},${cy(0)} Z` : '';
  const gridVals = Array.from({ length: 5 }, (_, i) => i * step);
  const hovered = hover !== null ? drawn.find((p) => p.i === hover) : undefined;

  return (
    <div ref={ref} className="relative w-full">
      {hovered && <Tip x={cx(hovered.i)} y={cy(hovered.value)}>{hovered.label} : <b>{fmtVal(hovered.value)}</b></Tip>}
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={view.caption} className="block">
        <defs>
          <linearGradient id="an-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={PALETTE.line} stopOpacity={0.35} />
            <stop offset="1" stopColor={PALETTE.line} stopOpacity={0} />
          </linearGradient>
        </defs>
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={cy(v)} y2={cy(v)} stroke={PALETTE.grid} />
            <text x={L - 8} y={cy(v) + 4} textAnchor="end" style={axisText}>{fmtAxis(v)}</text>
          </g>
        ))}
        {view.points.map((p, i) => <text key={p.label} x={cx(i)} y={H - 10} textAnchor="middle" style={axisText}>{p.label}</text>)}
        <path d={area} fill="url(#an-area)" />
        <polyline points={pts} fill="none" stroke={PALETTE.line} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {drawn.map((p) => (
          <circle key={p.i} cx={cx(p.i)} cy={cy(p.value)} r={hover === p.i ? 6.5 : 5} fill={PALETTE.line} stroke={PALETTE.ink} strokeWidth={2} />
        ))}
        {[first, last].filter(Boolean).map((p, k) => (
          <text key={k} x={cx(p.i)} y={cy(p.value) - 12} textAnchor="middle" style={{ fill: PALETTE.text, fontSize: 13, fontWeight: 600 }}>{fmtVal(p.value)}</text>
        ))}
        {view.points.map((p, i) => p.value === null ? (
          <text key={i} x={cx(i)} y={cy(0) - 8} textAnchor="middle" style={{ ...axisText, fontSize: 11 }}>en cours</text>
        ) : (
          <rect key={i} x={cx(i) - (W - L - R) / n / 2} y={T} width={(W - L - R) / n} height={H - T - B} fill="transparent"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
      </svg>
    </div>
  );
};

function niceMax(v: number) {
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(v, 1))));
  const m = v / pow;
  const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 4 ? 4 : m <= 5 ? 5 : m <= 8 ? 8 : 10;
  return nice * pow;
}

/* ── Barres groupées ───────────────────────────────────────────────────── */
export const GroupedBarsChart: React.FC<{ view: GroupedBarsView }> = ({ view }) => {
  const [ref, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const H = 300, L = 56, R = 16, T = 28, B = 36;
  const max = niceMax(Math.max(...view.a.values, ...view.b.values, 1));
  const n = view.labels.length;
  const band = (W - L - R) / n;
  const bw = Math.min(26, band * 0.32);
  const cx = (i: number) => L + band * (i + 0.5);
  const cy = (v: number) => T + (H - T - B) * (1 - v / max);
  const y0 = cy(0);
  return (
    <div ref={ref} className="relative w-full">
      {hover !== null && (
        <Tip x={cx(hover)} y={Math.min(cy(view.a.values[hover]), cy(view.b.values[hover]))}>
          {view.labels[hover]}<br />{view.a.name} : <b>{fmtInt(view.a.values[hover])}</b><br />{view.b.name} : <b>{fmtInt(view.b.values[hover])}</b>
        </Tip>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={view.caption} className="block">
        {Array.from({ length: 5 }, (_, i) => (i * max) / 4).map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={cy(v)} y2={cy(v)} stroke={PALETTE.grid} />
            <text x={L - 8} y={cy(v) + 4} textAnchor="end" style={axisText}>{fmtInt(Math.round(v))}</text>
          </g>
        ))}
        {view.labels.map((l, i) => <text key={l} x={cx(i)} y={H - 10} textAnchor="middle" style={axisText}>{l}</text>)}
        {view.labels.map((_, i) => (
          <g key={i} opacity={hover === null || hover === i ? 1 : 0.45}>
            <rect x={cx(i) - bw - 1} y={cy(view.a.values[i])} width={bw} height={Math.max(0, y0 - cy(view.a.values[i]))} rx={4} fill={PALETTE.magenta} />
            <rect x={cx(i) + 1} y={cy(view.b.values[i])} width={bw} height={Math.max(0, y0 - cy(view.b.values[i]))} rx={4} fill={PALETTE.ochre} />
            <rect x={cx(i) - band / 2} y={T} width={band} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
          </g>
        ))}
      </svg>
      <div className="mt-1 flex items-center gap-5 text-xs" style={{ color: PALETTE.axis }}>
        <span className="inline-flex items-center gap-2"><i className="inline-block h-3 w-3 rounded-sm" style={{ background: PALETTE.magenta }} />{view.a.name}</span>
        <span className="inline-flex items-center gap-2"><i className="inline-block h-3 w-3 rounded-sm" style={{ background: PALETTE.ochre }} />{view.b.name}</span>
      </div>
    </div>
  );
};

/* ── Heatmap midi / soir ───────────────────────────────────────────────── */
export const HeatmapChart: React.FC<{ view: HeatmapView }> = ({ view }) => {
  const all = view.rows.flatMap((r) => r.values);
  const mx = Math.max(...all, 1);
  const ramp = (v: number) => PALETTE.ordinal[Math.min(4, Math.floor((v / mx) * 4.999))];
  return (
    <div className="w-full">
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `3.25rem repeat(${view.days.length}, minmax(0, 1fr))` }}>
        {view.rows.map((row) => (
          <React.Fragment key={row.name}>
            <div className="flex items-center text-xs" style={{ color: PALETTE.axis }}>{row.name}</div>
            {row.values.map((v, i) => {
              const c = ramp(v);
              const dark = PALETTE.ordinal.indexOf(c) >= 3;
              return (
                <div
                  key={i}
                  title={`${view.days[i]} ${row.name.toLowerCase()} : ${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} visite(s) par semaine`}
                  className="flex h-20 items-center justify-center rounded-xl text-sm font-semibold transition-transform hover:scale-[1.03]"
                  style={{ background: v === 0 ? 'rgba(255,245,243,0.06)' : c, color: v === 0 ? PALETTE.axis : dark ? PALETTE.text : PALETTE.ink }}
                >
                  {v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        <div />
        {view.days.map((d) => <div key={d} className="text-center text-xs pt-1" style={{ color: PALETTE.axis }}>{d}</div>)}
      </div>
      <div className="mt-3 flex items-center gap-5 text-xs" style={{ color: PALETTE.axis }}>
        <span className="inline-flex items-center gap-2"><i className="inline-block h-3 w-3 rounded-sm" style={{ background: PALETTE.ordinal[0] }} />Calme</span>
        <span className="inline-flex items-center gap-2"><i className="inline-block h-3 w-3 rounded-sm" style={{ background: PALETTE.ordinal[4] }} />Plein</span>
      </div>
    </div>
  );
};

/* ── Donut ─────────────────────────────────────────────────────────────── */
export const DonutChart: React.FC<{ view: DonutView }> = ({ view }) => {
  const [hover, setHover] = useState<number | null>(null);
  const total = view.segments.reduce((s, x) => s + x.value, 0) || 1;
  const size = 240, cx = 120, cy = 120, r = 104, rw = 32;
  let a0 = -Math.PI / 2;
  const P = (rr: number, a: number) => `${cx + rr * Math.cos(a)},${cy + rr * Math.sin(a)}`;
  const arcs = view.segments.map((seg, i) => {
    const a1 = a0 + (seg.value / total) * 2 * Math.PI;
    const gap = view.segments.length > 1 ? 0.012 : 0;
    const s0 = a0 + gap, s1 = Math.max(s0, a1 - gap);
    const large = s1 - s0 > Math.PI ? 1 : 0;
    const d = `M${P(r, s0)} A${r},${r} 0 ${large} 1 ${P(r, s1)} L${P(r - rw, s1)} A${r - rw},${r - rw} 0 ${large} 0 ${P(r - rw, s0)} Z`;
    a0 = a1;
    return { d, color: PALETTE.ordinal[i % PALETTE.ordinal.length], seg, pct: Math.round((seg.value / total) * 1000) / 10 };
  });
  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={view.caption} className="flex-shrink-0">
        {arcs.map((a, i) => (
          <path key={i} d={a.d} fill={a.color} opacity={hover === null || hover === i ? 1 : 0.4} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <title>{a.seg.name} : {fmtInt(a.seg.value)} ({fmtPct(a.pct)})</title>
          </path>
        ))}
        <text x={cx} y={cy - 2} textAnchor="middle" style={{ fill: PALETTE.text, fontSize: 28, fontFamily: 'var(--font-stat)' }}>{fmtInt(hover !== null ? arcs[hover].seg.value : view.total)}</text>
        <text x={cx} y={cy + 20} textAnchor="middle" style={{ ...axisText }}>{hover !== null ? arcs[hover].seg.name : view.totalLabel}</text>
      </svg>
      <ul className="w-full space-y-2.5">
        {arcs.map((a, i) => (
          <li key={i} className="flex items-center gap-3 text-sm" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <i className="inline-block h-3 w-3 flex-shrink-0 rounded-sm" style={{ background: a.color }} />
            <span className="flex-1 font-medium" style={{ color: PALETTE.text }}>{a.seg.name}</span>
            <span style={{ color: PALETTE.axis }}>{fmtInt(a.seg.value)} · {fmtPct(a.pct)}</span>
          </li>
        ))}
        {arcs.length === 0 && <li className="text-sm" style={{ color: PALETTE.axis }}>Aucun membre pour le moment.</li>}
      </ul>
    </div>
  );
};

/* ── Barres horizontales ───────────────────────────────────────────────── */
export const HBarsChart: React.FC<{ view: HBarsView }> = ({ view }) => {
  const mx = Math.max(...view.rows.map((r) => r.value), 1);
  return (
    <ul className="w-full space-y-3">
      {view.rows.map((row) => (
        <li key={row.name} className="grid items-center gap-3" style={{ gridTemplateColumns: '5.5rem 1fr 4rem' }} title={`${row.name} par mois : ${fmtPct(row.value)} des membres`}>
          <span className="text-sm" style={{ color: PALETTE.axis }}>{row.name}</span>
          <div className="h-7 rounded-md" style={{ background: 'rgba(255,245,243,0.06)' }}>
            <div className="h-7 rounded-md transition-[width] duration-500" style={{ width: `${(row.value / mx) * 100}%`, background: PALETTE.line }} />
          </div>
          <span className="text-sm font-semibold text-right" style={{ color: PALETTE.text }}>{fmtPct(row.value)}</span>
        </li>
      ))}
    </ul>
  );
};

/* ── Sparkline des KPI ─────────────────────────────────────────────────── */
export const Sparkline: React.FC<{ values: number[]; width?: number; height?: number }> = ({ values, width = 90, height = 26 }) => {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const n = values.length;
  const pts = values.map((v, i) => `${(i / Math.max(1, n - 1)) * (width - 4) + 2},${height - 3 - ((v - min) / Math.max(1e-9, max - min)) * (height - 6)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="flex-shrink-0">
      <polyline points={pts} fill="none" stroke={PALETTE.line} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
